import shortid from 'shortid'
/**
 * Component definition helper to reduce boilerplace
 */

export function Component(name, config) {
  return {
    ...config,
    name,
    label: config.label || name.replace(/([A-Z])/g, ' $1').trim(),
    hasChildren: !!config.hasChildren,
    defaultContent: {
      ...config.defaultContent,
      meta: { ...(config.defaultContent.meta || {}), component: name, id: config.defaultContent.meta?.id || shortid() },
    },
    fields: config.fields || [],
    hasStyles: config.hasStyles == null ? true : config.hasStyles,
  }
}

/**
 * Expected parameter types:
 *
 * name           {string}
 * label          {string}
 * hidden         {Boolean true OR function(component): Boolean}
 *
 * options        {Array<{label, value}>}      required
 * filter         {function(item, component)}  optional
 * screenFilter   {function(item)}             optional
 * productFilter  {function(item)}             optional
 * multiple       {Boolean}                    default: false
 * defaultChecked {Boolean}                    optional
 * min            {number}                     default: 0
 * max            {number}                     default: 30
 *
 */
export const Color = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'color',
})

export const CssProperties = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'cssProperties',
})

export const Dropdown = ({
  name,
  label,
  tooltip = null,
  hidden = () => false,
  options,
  filter,
  getOptions,
  ...rest
}) => ({
  name,
  label,
  tooltip,
  hidden,
  options,
  filter,
  getOptions,
  type: 'dropdown',
  ...rest,
})
export const Form = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'form',
})
export const Icon = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'icon',
})
export const Image = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'image',
})
export const Video = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'video',
})
export const MultiPicker = ({ name, label, tooltip = null, options, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  options,
  type: 'multiPicker',
})
export const Number = ({ name, label, tooltip = null, hidden = () => false, min, max, step = 1 }) => ({
  name,
  label,
  tooltip,
  hidden,
  min,
  max,
  step,
  type: 'number',
})
export const PlainTextEditor = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'plainTextEditor',
})
export const Product = ({ name, label, tooltip = null, hidden = () => false, filter, emptyOptionLabel = '---' }) => ({
  name,
  label,
  tooltip,
  hidden,
  filter,
  emptyOptionLabel,
  type: 'products',
})
export const Products = ({ name, label, tooltip = null, hidden = () => false, filter }) => ({
  name,
  label,
  tooltip,
  hidden,
  filter,
  type: 'products',
  multiple: true,
})
export const Radio = ({ name, label, tooltip = null, options, hidden = () => false, defaultValue = null }) => ({
  name,
  label,
  defaultValue,
  tooltip,
  options,
  hidden,
  type: 'radio',
})
export const RichText = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'richText',
})
export const Screen = ({
  name,
  label,
  tooltip = null,
  hidden = () => false,
  filter,
  allowExternal = false,
  allowProduct = false,
  preset,
}) => ({
  name,
  label,
  tooltip,
  hidden,
  filter,
  allowExternal,
  allowProduct,
  preset,
  type: 'screens',
})
export const Screens = ({ name, label, tooltip = null, hidden = () => false, filter }) => ({
  name,
  label,
  tooltip,
  hidden,
  filter,
  type: 'screens',
  multiple: true,
})

export const ShippingOptionsEditor = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'shippingOptions',
})

export const Slider = ({ name, label, tooltip = null, hidden = () => false, min, max, step, displayAs }) => ({
  name,
  label,
  tooltip,
  hidden,
  min,
  step,
  displayAs,
  max,
  type: 'slider',
})
export const Switch = ({ name, label, tooltip = null, hidden = () => false, defaultChecked }) => ({
  name,
  label,
  tooltip,
  hidden,
  defaultChecked,
  type: 'switch',
})
export const Textarea = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'textarea',
})
export const Textfield = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'textfield',
})
export const ScoreEditor = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'scoreEditor',
})
export const OptionEditor = ({
  name,
  label,
  saveAs = 'array',
  columns = null,
  tooltip = null,
  hidden = () => false,
}) => ({
  name,
  label,
  columns,
  saveAs,
  tooltip,
  hidden,
  type: 'optionEditor',
})
/** `columns` may be an array or `(component) => array` (e.g. vertical-specific step columns). */
/** `emptyRow` optional object for each newly added step row (e.g. `{ contentState: {} }`). */
export const ProcessEditor = ({
  name,
  label,
  tooltip = null,
  hidden = () => false,
  columns = null,
  emptyRow = null,
}) => ({
  name,
  label,
  tooltip,
  hidden,
  columns,
  emptyRow,
  type: 'processEditor',
})
export const MultiField = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'multiField',
})

export const DxaSurvey = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'dxaSurvey',
})

export const Datastream = ({ name, label, tooltip = null, hidden = () => false }) => ({
  name,
  label,
  tooltip,
  hidden,
  type: 'datastream',
})

export const Section = (label, ...fields) => ({ label, fields, type: 'section' })

export const Custom = ({ render, hidden = () => false, name, isComponent = true }) => ({
  name,
  type: 'custom',
  render,
  hidden,
  isComponent,
})
